import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#854DFF] via-[#7A42E6] to-[#6F37CC] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center text-white hover:text-[#B19CFF] transition-colors duration-200 mb-6"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-[#B19CFF] max-w-3xl mx-auto">
              Last Updated: January 23, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            <h2 className="text-3xl font-bold text-gray-900 mb-6">1. Purpose of This Notice</h2>
            <p className="text-gray-700 mb-6">
              This privacy notice aims to give you information about how CredFlexi, a DBA (doing business as) of Essonsories Inc., collects and processes your personal data.
            </p>
            <p className="text-gray-700 mb-8">
              References in this notice to "you" or "your" are references to individuals whose personal data CredFlexi processes. References to "CredFlexi," "we," "us," or "our" are references to CredFlexi, which operates as a DBA under Essonsories Inc., a Delaware corporation headquartered at 16192 Coastal Highway, Lewes, Delaware 19958.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">2. Scope</h2>
            <p className="text-gray-700 mb-4">
              This privacy notice applies to the processing of personal data by CredFlexi in connection with:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-8 space-y-2">
              <li><strong>Client Services:</strong> Provision of agent-driven commerce platform services to actual and prospective enterprise clients</li>
              <li><strong>Supplier Services:</strong> Provision of products and services to CredFlexi by suppliers or service providers</li>
              <li><strong>Visitor Services:</strong> Provision of facilities and services to visitors at any CredFlexi premises</li>
              <li><strong>Recruitment Activities:</strong> Processing of candidate information for positions at CredFlexi</li>
              <li><strong>Website Usage:</strong> Processing of personal data through our website, including newsletter subscriptions, event registrations, and contact forms</li>
              <li><strong>Platform Usage:</strong> Processing of personal data through our agent-driven commerce platform and related services</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">3. Data Controller</h2>
            <p className="text-gray-700 mb-8">
              Essonsories Inc. (operating CredFlexi) is the data controller for your personal data processed under this policy. We are responsible for determining the purposes and means of processing your personal data.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">4. How to Make a Complaint</h2>
            <p className="text-gray-700 mb-4">
              If you have concerns about our processing of your personal data, please contact us at:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg mb-8">
              <div className="flex items-center space-x-3 mb-3">
                <Mail className="w-5 h-5 text-[#854DFF]" />
                <a href="mailto:privacy@credflexi.com" className="text-[#854DFF] hover:text-[#7A42E6]">
                  privacy@credflexi.com
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-[#854DFF]" />
                <span className="text-gray-700">For urgent privacy concerns</span>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">5. Personal Data We Collect</h2>
            <p className="text-gray-700 mb-4">
              "Personal Data" includes any information relating to an identified or identifiable individual. We collect the following categories of personal data:
            </p>
            
            <div className="overflow-x-auto mb-8">
              <table className="min-w-full bg-white border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Category</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Description and Examples</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-medium">Identity Data</td>
                    <td className="border border-gray-300 px-4 py-2">First name, last name, title, date of birth, photographs, gender</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-medium">Contact Data</td>
                    <td className="border border-gray-300 px-4 py-2">Business address, email address, telephone number</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-medium">Professional Information</td>
                    <td className="border border-gray-300 px-4 py-2">Job title, company name, business email, phone number</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-medium">Platform Data</td>
                    <td className="border border-gray-300 px-4 py-2">Account information, transaction data, platform usage, preferences</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-medium">Technical Data</td>
                    <td className="border border-gray-300 px-4 py-2">IP address, browser type, operating system, device information</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-medium">Usage Data</td>
                    <td className="border border-gray-300 px-4 py-2">Website interactions, platform usage patterns, pages visited</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">6. How We Collect Your Information</h2>
            <p className="text-gray-700 mb-4">
              We collect personal data through various methods:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-8 space-y-2">
              <li><strong>Direct Interactions:</strong> When you fill out forms, correspond with us, or use our services</li>
              <li><strong>Website and Platform:</strong> Through cookies, analytics, and platform usage</li>
              <li><strong>Third Parties:</strong> From business partners, service providers, and publicly available sources</li>
              <li><strong>Automated Technologies:</strong> Through server logs, cookies, and similar tracking technologies</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">7. Legal Basis and Purposes</h2>
            <p className="text-gray-700 mb-4">
              We process your personal data based on the following legal grounds:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-8 space-y-2">
              <li><strong>Contract Performance:</strong> To provide our agent-driven commerce platform services</li>
              <li><strong>Legal Obligations:</strong> To comply with legal and regulatory requirements</li>
              <li><strong>Legitimate Interests:</strong> To improve our services, ensure security, and grow our business</li>
              <li><strong>Consent:</strong> Where you have provided explicit consent</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">8. Data Sharing and Disclosures</h2>
            <p className="text-gray-700 mb-4">
              We may share your personal data with:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-8 space-y-2">
              <li>Service providers who assist in delivering our services</li>
              <li>Professional advisors (lawyers, auditors, consultants)</li>
              <li>Regulatory authorities when required by law</li>
              <li>Business partners for joint services (with your consent)</li>
              <li>Potential buyers in case of business transfer or merger</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">9. International Transfers</h2>
            <p className="text-gray-700 mb-8">
              As a global platform, we may transfer your personal data outside your country of residence. We ensure appropriate safeguards are in place, including adequacy decisions, standard contractual clauses, or other approved mechanisms to protect your data during international transfers.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">10. Data Security</h2>
            <p className="text-gray-700 mb-8">
              We implement appropriate technical and organizational measures to protect your personal data, including encryption, access controls, regular security assessments, and staff training on data protection.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">11. Data Retention</h2>
            <p className="text-gray-700 mb-8">
              We retain your personal data only as long as necessary to fulfill the purposes for which it was collected, comply with legal obligations, resolve disputes, and enforce our agreements. Specific retention periods vary based on the type of data and applicable legal requirements.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">12. Your Rights</h2>
            <p className="text-gray-700 mb-4">
              Depending on your jurisdiction, you may have the following rights:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-8 space-y-2">
              <li><strong>Access:</strong> Request information about your personal data we process</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate personal data</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data in certain circumstances</li>
              <li><strong>Restriction:</strong> Request limitation of processing in certain situations</li>
              <li><strong>Portability:</strong> Request transfer of your data to another controller</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Withdrawal of Consent:</strong> Withdraw consent where processing is based on consent</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">13. Cookies and Tracking</h2>
            <p className="text-gray-700 mb-8">
              Our website uses cookies and similar technologies to enhance your experience, analyze usage, and provide personalized content. You can manage your cookie preferences through your browser settings. For detailed information, please see our Cookie Policy.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">14. Children's Privacy</h2>
            <p className="text-gray-700 mb-8">
              Our services are designed for businesses and are not intended for individuals under 18 years of age. We do not knowingly collect personal data from children under 18. If we learn that we have collected such information, we will delete it promptly.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">15. Changes to This Policy</h2>
            <p className="text-gray-700 mb-8">
              We may update this privacy policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of material changes through our website or other appropriate means.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">16. Contact Information</h2>
            <div className="bg-[#854DFF]/5 p-6 rounded-lg mb-8">
              <p className="text-gray-700 mb-4">
                <strong>CredFlexi (DBA of Essonsories Inc.)</strong>
              </p>
              <p className="text-gray-700 mb-2">
                16192 Coastal Highway<br />
                Lewes, Delaware 19958<br />
                United States
              </p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-[#854DFF]" />
                  <a href="mailto:privacy@credflexi.com" className="text-[#854DFF] hover:text-[#7A42E6]">
                    privacy@credflexi.com
                  </a>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-[#854DFF]" />
                  <a href="mailto:info@credflexi.com" className="text-[#854DFF] hover:text-[#7A42E6]">
                    info@credflexi.com
                  </a>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <p className="text-sm text-gray-500 text-center">
                This privacy policy is effective as of January 23, 2025, and applies to all personal data processed by CredFlexi, a DBA of Essonsories Inc.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;